// Parser for Quake 3 / ET .shader scripts, following renderer/tr_shader.c:
// stage directives are parsed into typed structures and the per-stage defaults
// of ParseStage are applied here (rgbGen identity vs identityLighting, alphaGen
// vertex, depth write of blended stages, GL_ONE GL_ZERO = no blend). Defaults
// that depend on the surface (lightmap or vertex lit, implicit stages, sort)
// are resolved per surface by q3shader.ts.

import { tokenize } from '../shared/tokenize';

export type WaveFunc = 'sin' | 'triangle' | 'square' | 'sawtooth' | 'inversesawtooth' | 'noise';

export interface Wave {
  func: WaveFunc;
  base: number;
  amp: number;
  phase: number;
  freq: number;
}

export type BlendFactor =
  | 'GL_ONE'
  | 'GL_ZERO'
  | 'GL_DST_COLOR'
  | 'GL_ONE_MINUS_DST_COLOR'
  | 'GL_SRC_ALPHA'
  | 'GL_ONE_MINUS_SRC_ALPHA'
  | 'GL_DST_ALPHA'
  | 'GL_ONE_MINUS_DST_ALPHA'
  | 'GL_SRC_ALPHA_SATURATE'
  | 'GL_SRC_COLOR'
  | 'GL_ONE_MINUS_SRC_COLOR';

export type AlphaFunc = 'GT0' | 'LT128' | 'GE128' | null;

export type TcMod =
  | { type: 'turb'; wave: Wave }
  | { type: 'scale'; scale: [number, number] }
  | { type: 'scroll'; speed: [number, number] }
  | { type: 'stretch'; wave: Wave }
  | { type: 'transform'; matrix: [number, number, number, number]; translate: [number, number] }
  | { type: 'rotate'; speed: number }
  | { type: 'swap' }
  | { type: 'entityTranslate' };

export type RgbGen =
  | { type: 'identity' | 'identityLighting' | 'vertex' | 'exactVertex' | 'oneMinusVertex' | 'lightingDiffuse' | 'entity' | 'oneMinusEntity' }
  | { type: 'const'; color: [number, number, number] }
  | { type: 'wave'; wave: Wave };

export type AlphaGen =
  | { type: 'identity' | 'skip' | 'vertex' | 'oneMinusVertex' | 'entity' | 'oneMinusEntity' | 'lightingSpecular' | 'normalzfade' }
  | { type: 'const'; alpha: number }
  | { type: 'wave'; wave: Wave }
  | { type: 'portal'; range: number };

export type TcGen = { type: 'texture' | 'lightmap' | 'environment' | 'firerisenv' } | { type: 'vector'; s: [number, number, number]; t: [number, number, number] };

export type Deform =
  | { type: 'wave'; spread: number; wave: Wave }
  | { type: 'normal'; amp: number; freq: number }
  | { type: 'move'; vector: [number, number, number]; wave: Wave }
  | { type: 'bulge'; width: number; height: number; speed: number }
  | { type: 'autosprite' | 'autosprite2' | 'projectionShadow' | 'text' };

export interface ShaderStage {
  /** Texture path, "$lightmap" or "$whiteimage" (null: no image, the stage is dropped). */
  map: string | null;
  animMap: { freq: number; maps: string[] } | null;
  clamp: boolean;
  isLightmap: boolean;
  /** null = no blending (GL_ONE GL_ZERO is normalized to null). */
  blend: [BlendFactor, BlendFactor] | null;
  alphaFunc: AlphaFunc;
  depthWrite: boolean;
  depthFunc: 'lequal' | 'equal';
  /** null = not set; FinishShader picks lightmap or texture coordinates. */
  tcGen: TcGen | null;
  tcMods: TcMod[];
  rgbGen: RgbGen;
  alphaGen: AlphaGen;
  detail: boolean;
}

export interface SkyParms {
  /** Farbox base name ("env/sky" -> env/sky_rt.tga ...), null for "-". */
  farbox: string | null;
  /** 0 = no cloud layers (the skyparms had no cloud height). */
  cloudHeight: number;
  nearbox: string | null;
}

/** Shader sort keys (tr_local.h shaderSort_t). */
export const SORT = {
  portal: 1,
  sky: 2,
  opaque: 3,
  decal: 4,
  seeThrough: 5,
  banner: 6,
  fog: 7,
  underwater: 8,
  blend0: 9,
  additive: 10,
  nearest: 16,
} as const;

export interface ShaderDef {
  name: string;
  surfaceParms: Set<string>;
  cull: 'front' | 'back' | 'none';
  /** cull was given explicitly (implicit shaders only override the default). */
  cullExplicit: boolean;
  sky: SkyParms | null;
  stages: ShaderStage[];
  polygonOffset: boolean;
  /** Explicit sort key, or null. */
  sort: number | null;
  deforms: Deform[];
  fogParms: { color: [number, number, number]; depthForOpaque: number } | null;
  portal: boolean;
  noMipMaps: boolean;
  editorImage: string | null;
  implicitMap: string | null; // ET implicitMap/implicitMask/implicitBlend ('-' = the shader name)
  implicitKind: 'map' | 'mask' | 'blend' | null;
}


const stripExt = (p: string) => p.replace(/\.(tga|jpg|jpeg|png)$/i, '').toLowerCase();
const num = (s: string | undefined): number => {
  const v = parseFloat(s ?? '');
  return Number.isFinite(v) ? v : 0;
};

/** Words of a directive with "( a b c )" vectors flattened (parentheses may touch the numbers). */
function flatten(w: string[]): string[] {
  return w
    .join(' ')
    .replace(/[()]/g, ' ')
    .split(/\s+/)
    .filter(Boolean);
}

const WAVE_FUNCS: Record<string, WaveFunc> = { sin: 'sin', triangle: 'triangle', square: 'square', sawtooth: 'sawtooth', inversesawtooth: 'inversesawtooth', noise: 'noise' };

/** ParseWaveForm: <func> <base> <amplitude> <phase> <frequency> (unknown funcs are sin). */
function parseWave(w: string[], i: number): Wave {
  return { func: WAVE_FUNCS[(w[i] ?? '').toLowerCase()] ?? 'sin', base: num(w[i + 1]), amp: num(w[i + 2]), phase: num(w[i + 3]), freq: num(w[i + 4]) };
}

const SRC_FACTORS = new Set<BlendFactor>(['GL_ONE', 'GL_ZERO', 'GL_DST_COLOR', 'GL_ONE_MINUS_DST_COLOR', 'GL_SRC_ALPHA', 'GL_ONE_MINUS_SRC_ALPHA', 'GL_DST_ALPHA', 'GL_ONE_MINUS_DST_ALPHA', 'GL_SRC_ALPHA_SATURATE']);
const DST_FACTORS = new Set<BlendFactor>(['GL_ONE', 'GL_ZERO', 'GL_SRC_ALPHA', 'GL_ONE_MINUS_SRC_ALPHA', 'GL_DST_ALPHA', 'GL_ONE_MINUS_DST_ALPHA', 'GL_SRC_COLOR', 'GL_ONE_MINUS_SRC_COLOR']);

/** Stage being parsed, with the ParseStage locals that decide the defaults. */
interface StageWork {
  stage: ShaderStage;
  rgbGenSet: boolean;
  depthWriteExplicit: boolean;
}

function newStage(): StageWork {
  return {
    stage: {
      map: null,
      animMap: null,
      clamp: false,
      isLightmap: false,
      blend: null,
      alphaFunc: null,
      depthWrite: true,
      depthFunc: 'lequal',
      tcGen: null,
      tcMods: [],
      rgbGen: { type: 'identityLighting' },
      alphaGen: { type: 'identity' },
      detail: false,
    },
    rgbGenSet: false,
    depthWriteExplicit: false,
  };
}

/** End of ParseStage: defaults that depend on the whole stage. */
function finishStage(work: StageWork): ShaderStage {
  const s = work.stage;
  // if cgen isn't explicitly specified, use either identity or identitylighting
  if (!work.rgbGenSet) {
    const src = s.blend?.[0];
    s.rgbGen = { type: !src || src === 'GL_ONE' || src === 'GL_SRC_ALPHA' ? 'identityLighting' : 'identity' };
  }
  // implicitly assume that a GL_ONE GL_ZERO blend mask disables blending
  if (s.blend && s.blend[0] === 'GL_ONE' && s.blend[1] === 'GL_ZERO') {
    s.blend = null;
    s.depthWrite = true;
  }
  // decide which agens we can skip
  if (s.alphaGen.type === 'identity' && (s.rgbGen.type === 'identity' || s.rgbGen.type === 'lightingDiffuse')) s.alphaGen = { type: 'skip' };
  return s;
}

/** Parses a whole .shader file. */
export function parseShaderScript(src: string, out = new Map<string, ShaderDef>()): Map<string, ShaderDef> {
  const tokens = tokenize(src);
  let i = 0;

  // groups tokens of the current line starting at i
  const line = (): string[] => {
    const words = [tokens[i++].text];
    while (i < tokens.length && !tokens[i].newline && tokens[i].text !== '{' && tokens[i].text !== '}') words.push(tokens[i++].text);
    return words;
  };

  while (i < tokens.length) {
    const name = tokens[i++].text.toLowerCase();
    if (i >= tokens.length || tokens[i].text !== '{') {
      // malformed; resync on next line
      continue;
    }
    i++;
    const def: ShaderDef = {
      name: stripExt(name),
      surfaceParms: new Set(),
      cull: 'front',
      cullExplicit: false,
      sky: null,
      stages: [],
      polygonOffset: false,
      sort: null,
      deforms: [],
      fogParms: null,
      portal: false,
      noMipMaps: false,
      editorImage: null,
      implicitMap: null,
      implicitKind: null,
    };
    while (i < tokens.length && tokens[i].text !== '}') {
      if (tokens[i].text === '{') {
        i++;
        const work = newStage();
        while (i < tokens.length && tokens[i].text !== '}') parseStageDirective(work, line());
        i++; // }
        def.stages.push(finishStage(work));
        continue;
      }
      parseShaderDirective(def, line());
    }
    i++; // }
    if (!out.has(def.name)) out.set(def.name, def); // first definition wins, like Quake 3
  }
  return out;
}

function parseSort(v: string): number | null {
  const key = v.toLowerCase();
  const named: Record<string, number> = {
    portal: SORT.portal,
    sky: SORT.sky,
    opaque: SORT.opaque,
    decal: SORT.decal,
    seethrough: SORT.seeThrough,
    banner: SORT.banner,
    additive: SORT.additive,
    nearest: SORT.nearest,
    underwater: SORT.underwater,
  };
  if (key in named) return named[key];
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : null;
}

function parseShaderDirective(def: ShaderDef, w: string[]): void {
  const key = w[0].toLowerCase();
  switch (key) {
    case 'surfaceparm':
      if (w[1]) def.surfaceParms.add(w[1].toLowerCase());
      break;
    case 'cull': {
      if (!w[1]) break;
      const v = w[1].toLowerCase();
      def.cullExplicit = true;
      def.cull = v === 'none' || v === 'twosided' || v === 'disable' ? 'none' : v === 'back' || v === 'backside' || v === 'backsided' ? 'back' : 'front';
      break;
    }
    case 'skyparms': {
      // skyParms <outerbox> <cloudheight> <innerbox>
      const sky: SkyParms = { farbox: w[1] && w[1] !== '-' ? stripExt(w[1]) : null, cloudHeight: 0, nearbox: null };
      if (w[2] !== undefined) sky.cloudHeight = num(w[2]) || 512;
      if (w[3] && w[3] !== '-') sky.nearbox = stripExt(w[3]);
      def.sky = sky;
      break;
    }
    case 'polygonoffset':
      def.polygonOffset = true;
      break;
    case 'portal':
      def.portal = true;
      def.sort = SORT.portal;
      break;
    case 'sort':
      if (w[1]) def.sort = parseSort(w[1]);
      break;
    case 'nomipmaps':
    case 'nomipmap':
      def.noMipMaps = true;
      break;
    case 'fogparms': {
      const v = flatten(w.slice(1));
      def.fogParms = { color: [num(v[0]), num(v[1]), num(v[2])], depthForOpaque: Math.max(1, num(v[3]) || 1) };
      break;
    }
    case 'deformvertexes': {
      const d = parseDeform(w.slice(1));
      if (d) def.deforms.push(d);
      break;
    }
    case 'qer_editorimage':
      if (w[1]) def.editorImage = stripExt(w[1]);
      break;
    case 'implicitmap':
    case 'implicitmask':
    case 'implicitblend':
      def.implicitKind = key === 'implicitmap' ? 'map' : key === 'implicitmask' ? 'mask' : 'blend';
      def.implicitMap = w[1] && w[1] !== '-' ? stripExt(w[1]) : '-';
      break;
  }
}

/** ParseDeform */
function parseDeform(w: string[]): Deform | null {
  const kind = (w[0] ?? '').toLowerCase();
  switch (kind) {
    case 'wave': {
      const s = num(w[1]);
      return { type: 'wave', spread: s ? 1 / s : 100, wave: parseWave(w, 2) };
    }
    case 'normal':
      return { type: 'normal', amp: num(w[1]), freq: num(w[2]) };
    case 'move': {
      const v = flatten(w.slice(1));
      return { type: 'move', vector: [num(v[0]), num(v[1]), num(v[2])], wave: parseWave(v, 3) };
    }
    case 'bulge':
      return { type: 'bulge', width: num(w[1]), height: num(w[2]), speed: num(w[3]) };
    case 'autosprite':
    case 'autosprite2':
      return { type: kind };
    case 'projectionshadow':
      return { type: 'projectionShadow' };
  }
  if (kind.startsWith('text')) return { type: 'text' };
  return null;
}

/** ParseTexMod */
function parseTcMod(w: string[]): TcMod | null {
  switch ((w[0] ?? '').toLowerCase()) {
    case 'swap':
      return { type: 'swap' };
    case 'turb':
      // turb <base> <amplitude> <phase> <frequency> (no function name)
      return { type: 'turb', wave: { func: 'sin', base: num(w[1]), amp: num(w[2]), phase: num(w[3]), freq: num(w[4]) } };
    case 'scale':
      return { type: 'scale', scale: [num(w[1]), num(w[2])] };
    case 'scroll':
      return { type: 'scroll', speed: [num(w[1]), num(w[2])] };
    case 'stretch':
      return { type: 'stretch', wave: parseWave(w, 1) };
    case 'transform':
      return { type: 'transform', matrix: [num(w[1]), num(w[2]), num(w[3]), num(w[4])], translate: [num(w[5]), num(w[6])] };
    case 'rotate':
      return { type: 'rotate', speed: num(w[1]) };
    case 'entitytranslate':
      return { type: 'entityTranslate' };
  }
  return null;
}

function parseStageDirective(work: StageWork, w: string[]): void {
  const stage = work.stage;
  const key = w[0].toLowerCase();
  switch (key) {
    case 'map':
    case 'clampmap':
    case 'lightmap': {
      const m = (w[1] || '').toLowerCase();
      if (!m) break;
      stage.clamp = key !== 'map';
      if (m === '$lightmap') {
        stage.isLightmap = true;
        stage.map = '$lightmap';
      } else if (m === '$whiteimage' || m === '*white') stage.map = '$whiteimage';
      else if (m.startsWith('$')) stage.map = m;
      else {
        stage.map = stripExt(m);
        if (key === 'lightmap') stage.isLightmap = true;
      }
      break;
    }
    case 'animmap':
      stage.animMap = { freq: num(w[1]), maps: w.slice(2, 10).map(stripExt) };
      stage.map = stage.animMap.maps[0] ?? null;
      break;
    case 'blendfunc': {
      const a = (w[1] || '').toUpperCase();
      if (!a) break;
      if (a === 'ADD') stage.blend = ['GL_ONE', 'GL_ONE'];
      else if (a === 'FILTER') stage.blend = ['GL_DST_COLOR', 'GL_ZERO'];
      else if (a === 'BLEND') stage.blend = ['GL_SRC_ALPHA', 'GL_ONE_MINUS_SRC_ALPHA'];
      else {
        // unknown factors become GL_ONE, like NameToSrcBlendMode / NameToDstBlendMode
        const src = SRC_FACTORS.has(a as BlendFactor) ? (a as BlendFactor) : 'GL_ONE';
        const b = (w[2] || '').toUpperCase();
        if (!b) break;
        stage.blend = [src, DST_FACTORS.has(b as BlendFactor) ? (b as BlendFactor) : 'GL_ONE'];
      }
      // clear depth mask for blended surfaces
      if (!work.depthWriteExplicit) stage.depthWrite = false;
      break;
    }
    case 'alphafunc': {
      const v = (w[1] || '').toUpperCase();
      stage.alphaFunc = v === 'GT0' || v === 'LT128' || v === 'GE128' ? v : null;
      break;
    }
    case 'depthfunc': {
      const v = (w[1] || '').toLowerCase();
      if (v === 'equal') stage.depthFunc = 'equal';
      else if (v === 'lequal') stage.depthFunc = 'lequal';
      break;
    }
    case 'depthwrite':
      stage.depthWrite = true;
      work.depthWriteExplicit = true;
      break;
    case 'detail':
      stage.detail = true;
      break;
    case 'tcgen':
    case 'texgen': {
      const v = (w[1] || '').toLowerCase();
      if (v === 'environment') stage.tcGen = { type: 'environment' };
      else if (v === 'firerisenv') stage.tcGen = { type: 'firerisenv' };
      else if (v === 'lightmap') stage.tcGen = { type: 'lightmap' };
      else if (v === 'texture' || v === 'base') stage.tcGen = { type: 'texture' };
      else if (v === 'vector') {
        const n = flatten(w.slice(2)).map(num);
        stage.tcGen = { type: 'vector', s: [n[0] ?? 0, n[1] ?? 0, n[2] ?? 0], t: [n[3] ?? 0, n[4] ?? 0, n[5] ?? 0] };
      }
      break;
    }
    case 'tcmod': {
      const mod = parseTcMod(w.slice(1));
      if (mod && stage.tcMods.length < 4) stage.tcMods.push(mod);
      break;
    }
    case 'rgbgen': {
      const v = (w[1] || '').toLowerCase();
      const simple: Record<string, RgbGen['type']> = {
        identity: 'identity',
        identitylighting: 'identityLighting',
        entity: 'entity',
        oneminusentity: 'oneMinusEntity',
        vertex: 'vertex',
        exactvertex: 'exactVertex',
        lightingdiffuse: 'lightingDiffuse',
        oneminusvertex: 'oneMinusVertex',
      };
      if (v === 'wave') stage.rgbGen = { type: 'wave', wave: parseWave(w, 2) };
      else if (v === 'const') {
        const c = flatten(w.slice(2)).map(num);
        stage.rgbGen = { type: 'const', color: [c[0] ?? 0, c[1] ?? 0, c[2] ?? 0] };
      } else if (simple[v]) {
        stage.rgbGen = { type: simple[v] } as RgbGen;
        if (v === 'vertex' && stage.alphaGen.type === 'identity') stage.alphaGen = { type: 'vertex' };
      } else break;
      work.rgbGenSet = true;
      break;
    }
    case 'alphagen': {
      const v = (w[1] || '').toLowerCase();
      const simple: Record<string, AlphaGen['type']> = {
        identity: 'identity',
        entity: 'entity',
        oneminusentity: 'oneMinusEntity',
        vertex: 'vertex',
        lightingspecular: 'lightingSpecular',
        oneminusvertex: 'oneMinusVertex',
        normalzfade: 'normalzfade',
      };
      if (v === 'wave') stage.alphaGen = { type: 'wave', wave: parseWave(w, 2) };
      else if (v === 'const') stage.alphaGen = { type: 'const', alpha: num(w[2]) };
      else if (v === 'portal') stage.alphaGen = { type: 'portal', range: w[2] ? num(w[2]) : 256 };
      else if (simple[v]) stage.alphaGen = { type: simple[v] } as AlphaGen;
      else break;
      break;
    }
  }
}

/** Does the blend read the framebuffer (so the stage cannot start a single pass)? */
export function blendReadsDst(blend: [BlendFactor, BlendFactor] | null): boolean {
  if (!blend) return false;
  const [s, d] = blend;
  return d !== 'GL_ZERO' || s === 'GL_DST_COLOR' || s === 'GL_ONE_MINUS_DST_COLOR' || s === 'GL_DST_ALPHA' || s === 'GL_ONE_MINUS_DST_ALPHA' || s === 'GL_SRC_ALPHA_SATURATE';
}
